import type {
  PublicCurpParseResponse,
  PublicRegistrationPayload,
  PublicRegistrationPlayerProfile,
  PublicRegistrationSubmitInput,
  PublicWhoPlays,
  RegistrationFormField,
} from "./api";
import {
  classifyCurp,
  curpFactsFromParse,
  curpReviewSignature,
  sameNameText,
  tokens,
  type CurpFacts,
  type CurpNameParts,
  type CurpRegistered,
} from "./curpClassifier";
import { fieldSection, suggestsGuardianSection } from "./registrationFields";
import { divisionBirthYearRange } from "./tournamentDivisions";

// «¿Quién va a jugar?» en el formulario público (Eduardo 2026-10-01).
//
// Problema: en el torneo 59 los papás escribían SU nombre en los datos del
// jugador (7 CURP que no coincidían) y nadie leía el párrafo «Importante: …».
// Ahora la primera pregunta separa «Mi hijo o hija» (el papá o la mamá es el
// dueño de la Cuenta Familiar; el jugador es el perfil) de «Yo» (igual que
// siempre). Aquí vive la lógica pura de la pantalla, para probarla sin montar
// la página.

export type RegistrationFormState = PublicRegistrationSubmitInput & {
  privacy_notice_accepted: boolean;
  custom_answers: Record<string, string | boolean>;
};

export const initialRegistrationForm: RegistrationFormState = {
  division_ids: [],
  player_profile_id: null,
  first_name: "",
  last_name: "",
  maternal_last_name: "",
  phone: "",
  email: "",
  birthdate: "",
  gender: "",
  nationality_code: "MEX",
  state_province: "",
  city: "",
  club_name: "",
  national_federation_id: "",
  wtn_tennis_id: "",
  dominant_hand: "",
  backhand_type: "",
  guardian_name: "",
  guardian_phone: "",
  guardian_relationship: "",
  guardian_email: "",
  coach_name: "",
  coach_phone: "",
  coach_email: "",
  data_consent: false,
  privacy_notice_accepted: false,
  photo_file: null,
  curp_pdf_file: null,
  payment_receipt_file: null,
  custom_answers: {},
};

// ---------------------------------------------------------------- modo

export interface WhoPlaysSettings {
  ask: boolean;
  default: PublicWhoPlays;
}

// Eduardo 2026-10-04: «18 y menores sí cuenta como categoría de menores»
// (antes el tope era 17). Espejo de `KIDS_MAX_AGE` del servidor.
const KIDS_MAX_AGE = 18;

/** Nombre que dice que es de menores (solo cuenta si la categoría no tiene
 * ningún año capturado): «N y menores» con N ≤ 18; «menores de N», «sub N»
 * y «U N» con N ≤ 19 («Sub-18», «U18» y «18 y menores» cuentan).
 * «Infantil» o «Juvenil» sin número no cuentan, ni Sub-21 ni U23. Un número con decimales es un NIVEL, no una edad («3.5 y
 * menores», «Dobles 7.0 y menores», «4,5 y menores», «4.0 u 4.5»): no cuenta
 * ni el número antes del punto o la coma ni el de después. Espejo de
 * `division_name_says_minors` del servidor. */
const MINORS_NAME_RULES: ReadonlyArray<[RegExp, number]> = [
  // Sin «lookbehind»: un iPhone con Safari viejo no carga la página con él.
  // `(?![.,]?\d)`: el número no sigue con decimales.
  [/(?:^|[^\d.,])(\d{1,2})(?![.,]?\d)\s*(?:anos\s*)?y\s*menores/g, KIDS_MAX_AGE],
  [/menores\s+de\s+(\d{1,2})(?![.,]?\d)/g, KIDS_MAX_AGE + 1],
  [/\bsub[\s-]?(\d{1,2})(?![.,]?\d)\b/g, KIDS_MAX_AGE + 1],
  [/\bu[\s-]?(\d{1,2})(?![.,]?\d)\b/g, KIDS_MAX_AGE + 1],
];

export function divisionNameSaysMinors(name: string | null | undefined): boolean {
  const text = String(name ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return MINORS_NAME_RULES.some(([pattern, maxNumber]) =>
    Array.from(text.matchAll(pattern)).some((match) => Number(match[1]) <= maxNumber),
  );
}

/** Categoría de menores (Eduardo 2026-10-03 y 2026-10-04): ACTIVA y todos
 * sus jugadores tienen 18 años o menos al empezar el torneo. Con años:
 * «Nacidos desde» (F, las edades viejas se convierten) cumple a lo más 18 en
 * el año del torneo (Y − F ≤ 18). Sin ningún año capturado decide el nombre
 * (`divisionNameSaysMinors`). Misma regla que `division_is_minors` del
 * servidor, que también decide el «¿Quién va a jugar?» del link público. */
export function isKidsDivision(
  division: { name?: string | null; age_min?: number | null; age_max?: number | null; is_active?: boolean | null },
  tournamentYear: number,
): boolean {
  if (division.is_active === false) return false;
  const range = divisionBirthYearRange(division, tournamentYear);
  if (range === null) return divisionNameSaysMinors(division.name);
  return range.from != null && tournamentYear - range.from <= KIDS_MAX_AGE;
}

/** Lo que mandó el servidor. Sin la llave (servidor viejo) no se pregunta. */
export function whoPlaysConfig(payload: Pick<PublicRegistrationPayload, "who_plays"> | null | undefined): WhoPlaysSettings {
  const raw = payload?.who_plays;
  if (!raw || typeof raw !== "object" || !raw.ask) return { ask: false, default: "self" };
  return { ask: true, default: raw.default === "child" ? "child" : "self" };
}

// ---------------------------------------------------------------- edad y nombres

function isoParts(value: string | null | undefined): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? "").trim());
  if (!match) return null;
  const parts: [number, number, number] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return parts[1] >= 1 && parts[1] <= 12 && parts[2] >= 1 && parts[2] <= 31 ? parts : null;
}

/** Edad cumplida el día que empieza el torneo (o hoy si no hay fecha). */
export function ageOnDate(
  birthdate: string | null | undefined,
  onDate: string | null | undefined,
  today: Date = new Date(),
): number | null {
  const born = isoParts(birthdate);
  if (!born) return null;
  const reference = isoParts(onDate) ?? [today.getFullYear(), today.getMonth() + 1, today.getDate()];
  let age = reference[0] - born[0];
  if (reference[1] < born[1] || (reference[1] === born[1] && reference[2] < born[2])) age -= 1;
  return age >= 0 ? age : null;
}

/** Nombre completo para mostrar: «LEO LÓPEZ DE LA CRUZ» → «Leo López de la Cruz». */
export function displayPersonName(value: string | null | undefined): string {
  const small = new Set(["de", "del", "la", "las", "los", "y"]);
  return String(value ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLocaleLowerCase("es-MX");
      if (index > 0 && small.has(lower)) return lower;
      return lower.charAt(0).toLocaleUpperCase("es-MX") + lower.slice(1);
    })
    .join(" ");
}

export function playerFullName(form: Pick<PublicRegistrationSubmitInput, "first_name" | "last_name" | "maternal_last_name">): string {
  return [form.first_name, form.last_name, form.maternal_last_name]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

/** El papá o la mamá escribió SU nombre en los datos del jugador: nombre y
 * primer apellido del jugador están dentro de su nombre (sin acentos ni
 * mayúsculas). Si escribió sus dos apellidos y el jugador trae segundo
 * apellido, también cuenta: «Eduardo Pérez García» inscribe a su hijo
 * «Eduardo Pérez López» y no es su nombre. */
export function guardianNameMatchesPlayer(
  guardianFull: string | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  maternalLastName?: string | null,
): boolean {
  const first = tokens(firstName);
  const last = tokens(lastName);
  if (!first.length || !last.length) return false;
  const guardianWords = tokens(guardianFull);
  const guardian = new Set(guardianWords);
  const maternal = guardianWords.length >= 3 ? tokens(maternalLastName) : [];
  return [...first, ...last, ...maternal].every((word) => guardian.has(word));
}

/** Firma de «Ese es tu nombre»: «El nombre está bien, continuar» la guarda y
 * el aviso vuelve si cambia cualquiera de los dos nombres. */
export function ownNameSignature(
  form: Pick<RegistrationFormState, "guardian_name" | "first_name" | "last_name" | "maternal_last_name">,
): string {
  return [form.guardian_name, form.first_name, form.last_name, form.maternal_last_name]
    .map((value) => tokens(value).join(" "))
    .join("|");
}

/** La CURP subida parece la del papá o la mamá: su nombre y primer apellido
 * (al menos 2 palabras) están dentro del nombre que escribió en «Tus datos».
 *
 * No lo es si la CURP es de un menor (nadie es tutor a los 12 años) ni,
 * cuando el papá escribió sus dos apellidos, si el segundo apellido de la
 * CURP no está en su nombre: «Eduardo Pérez Ruiz» sube la de su hijo
 * «Eduardo Pérez García». */
export function curpLooksLikeGuardian(
  facts: CurpFacts | null | undefined,
  guardianFull: string | null | undefined,
  onDate?: string | null,
): boolean {
  const words = [...tokens(facts?.name?.given), ...tokens(facts?.name?.paternal)];
  if (words.length < 2) return false;
  const age = ageOnDate(facts?.birthdate, onDate);
  if (age !== null && age < 18) return false;
  const guardianWords = tokens(guardianFull);
  const guardian = new Set(guardianWords);
  const maternal = guardianWords.length >= 3 ? tokens(facts?.name?.maternal) : [];
  return [...words, ...maternal].every((word) => guardian.has(word));
}

// ---------------------------------------------------------------- tutor y cuenta

export interface SessionAccount {
  full_name: string | null;
  email: string | null;
  phone: string | null;
}

/** Con sesión, «Tus datos» arranca con los de la cuenta: nombre y celular
 * solo si están vacíos. El correo SIEMPRE es el de la cuenta: la caja se ve
 * sin editar y dice «Es el correo de tu cuenta» (un «Email del tutor» viejo
 * del perfil no se queda ahí). Una cuenta vieja con el nombre de un hijo
 * (`familyNames`) no pone a ese hijo en «Tu nombre». */
export function fillGuardianFromAccount(
  form: RegistrationFormState,
  account: SessionAccount | null | undefined,
  familyNames: ReadonlyArray<string | null | undefined> = [],
): RegistrationFormState {
  if (!account) return form;
  const accountName = account.full_name?.trim() || "";
  const usableName = accountName && !familyNames.some((item) => sameNameText(item, accountName)) ? accountName : "";
  const name = form.guardian_name?.trim() ? form.guardian_name : usableName || form.guardian_name;
  const email = account.email?.trim() || form.guardian_email;
  const phone = form.guardian_phone?.trim() ? form.guardian_phone : account.phone?.trim() || form.guardian_phone;
  if (name === form.guardian_name && email === form.guardian_email && phone === form.guardian_phone) return form;
  return { ...form, guardian_name: name ?? "", guardian_email: email ?? "", guardian_phone: phone ?? "" };
}

/** Elegir un perfil de la familia en modo hijo no borra «Tus datos»: manda lo
 * que ya está escrito, luego lo del perfil y al final lo de la cuenta. */
export function keepGuardianBox(
  current: RegistrationFormState,
  next: RegistrationFormState,
  account: SessionAccount | null | undefined,
  familyNames: ReadonlyArray<string | null | undefined> = [],
): RegistrationFormState {
  const pick = (key: "guardian_name" | "guardian_phone" | "guardian_relationship" | "guardian_email") =>
    String(current[key] ?? "").trim() ? current[key] : next[key];
  return fillGuardianFromAccount(
    {
      ...next,
      guardian_name: pick("guardian_name") ?? "",
      guardian_phone: pick("guardian_phone") ?? "",
      guardian_relationship: pick("guardian_relationship") ?? "",
      guardian_email: pick("guardian_email") ?? "",
    },
    account,
    familyNames,
  );
}

/** Nombres de los perfiles de la cuenta que son de un HIJO (traen tutor o
 * cumplen 18 años o menos en el año del torneo, la misma edad que una
 * categoría de menores). Si la cuenta se llama como uno de ellos,
 * se armó desde ese hijo en el flujo viejo; el perfil adulto del propio papá
 * no cuenta (su nombre sí es el de la cuenta). */
export function childProfileNames(
  profiles: ReadonlyArray<Pick<PublicRegistrationPlayerProfile, "birthdate" | "display_name" | "guardian_name">>,
  tournamentYear: number,
): string[] {
  return profiles
    .filter((profile) => {
      if (String(profile.guardian_name ?? "").trim()) return true;
      const born = isoParts(profile.birthdate);
      return Boolean(born && tournamentYear - born[0] <= KIDS_MAX_AGE);
    })
    .map((profile) => profile.display_name);
}

/** Con sesión y en «Mi hijo o hija», ¿se precarga el único perfil de la
 * cuenta en «Datos de quien va a jugar»? No si es un adulto (no cabe en una
 * categoría infantil: cumple más de 18 en el año del torneo; «18 y menores»
 * sí es de menores, Eduardo 2026-10-04) ni si es el titular de la cuenta sin
 * fecha: es el papá, y en la caja 2 va el hijo. */
export function shouldPreloadInChildMode(
  profile: Pick<PublicRegistrationPlayerProfile, "birthdate" | "display_name" | "guardian_name">,
  account: SessionAccount | null | undefined,
  tournamentYear: number,
): boolean {
  const born = isoParts(profile.birthdate);
  if (born) return tournamentYear - born[0] <= KIDS_MAX_AGE;
  const holder = Boolean(account?.full_name && sameNameText(account.full_name, profile.display_name));
  return !(holder && !String(profile.guardian_name ?? "").trim());
}

/** Correo con el que queda la cuenta. */
export function accountEmailOf(form: Pick<RegistrationFormState, "email" | "guardian_email">, whoPlays: PublicWhoPlays): string {
  if (whoPlays === "child") return String(form.guardian_email || form.email || "").trim().toLowerCase();
  return String(form.email ?? "").trim().toLowerCase();
}

/** Qué casillas lleva «Tus datos» en modo hijo. Nombre y correo siempre (la
 * cuenta es del papá o la mamá); celular si el torneo pide celular;
 * parentesco si el torneo lo pide. */
export function guardianBoxFields(fields: RegistrationFormField[]): { phone: boolean; relationship: boolean } {
  const enabled = (key: string) => fields.some((field) => field.key === key && field.enabled !== false);
  return { phone: enabled("phone") || enabled("guardian_phone"), relationship: enabled("guardian_relationship") };
}

export const CHILD_MODE_LABELS = {
  guardian_name: "Tu nombre (papá, mamá o tutor)",
  guardian_phone: "Tu celular",
  guardian_email: "Tu correo",
  guardian_relationship: "Parentesco",
} as const;

/** «Falta completar» en modo hijo: un «Nombre» suelto junto a «Tu nombre» no
 * dice de quién es. Mismas etiquetas que el servidor (who_plays.py). */
export const CHILD_MODE_PLAYER_LABELS: Readonly<Record<string, string>> = {
  first_name: "Nombre del jugador",
  last_name: "Primer apellido del jugador",
  maternal_last_name: "Segundo apellido del jugador",
};

/** Lo que falta en «Tus datos» (modo hijo), con las etiquetas de la pantalla. */
export function childModeMissingGuardian(form: RegistrationFormState, fields: RegistrationFormField[]): string[] {
  const required = (key: string) =>
    fields.some((field) => field.key === key && field.enabled !== false && Boolean(field.required));
  const empty = (value: string | null | undefined) => !String(value ?? "").trim();
  const missing: string[] = [];
  if (empty(form.guardian_name)) missing.push(CHILD_MODE_LABELS.guardian_name);
  if ((required("phone") || required("guardian_phone")) && empty(form.guardian_phone)) missing.push(CHILD_MODE_LABELS.guardian_phone);
  if (empty(form.guardian_email)) missing.push(CHILD_MODE_LABELS.guardian_email);
  if (required("guardian_relationship") && empty(form.guardian_relationship)) missing.push(CHILD_MODE_LABELS.guardian_relationship);
  return missing;
}

/** Campos que en modo hijo NO se validan como del jugador: el celular y el
 * correo son los de «Tus datos», y los del tutor se revisan aparte. */
export function skippedInChildMode(field: Pick<RegistrationFormField, "key" | "kind">): boolean {
  if (field.key === "phone" || field.key === "email") return true;
  return field.kind !== "custom" && fieldSection({ key: field.key }) === "guardian";
}

// ---------------------------------------------------------------- envío

export interface SubmitOptions {
  whoPlays: PublicWhoPlays;
  /** Mandar `who_plays` aunque sea «self» (solo cuando se preguntó). */
  sendWhoPlays: boolean;
  isMinor: boolean;
  gender: string;
  feeCategory: string;
}

/** Lo que se manda al servidor. En modo hijo: `who_plays=child`, los datos
 * del tutor a cualquier edad y el correo/celular de la cuenta = los del
 * tutor. En «Yo», exactamente lo de siempre. */
export function buildSubmitInput(form: RegistrationFormState, options: SubmitOptions): PublicRegistrationSubmitInput {
  const { privacy_notice_accepted: _privacy, custom_answers: customAnswers, ...submitForm } = form;
  void _privacy;
  const child = options.whoPlays === "child";
  const guardianEmail = form.guardian_email?.trim().toLowerCase() || "";
  const guardianPhone = form.guardian_phone?.trim() || "";
  const includeGuardian = child || options.isMinor;
  const input: PublicRegistrationSubmitInput = {
    ...submitForm,
    custom_answers_json: JSON.stringify(customAnswers ?? {}),
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    maternal_last_name: form.maternal_last_name?.trim() || null,
    phone: child ? guardianPhone || form.phone.trim() : form.phone.trim(),
    email: child ? guardianEmail || form.email.trim().toLowerCase() : form.email.trim().toLowerCase(),
    gender: options.gender,
    nationality_code: form.nationality_code.trim().toUpperCase(),
    state_province: form.state_province.trim(),
    city: form.city.trim(),
    club_name: form.club_name.trim(),
    national_federation_id: form.national_federation_id?.trim().toUpperCase() || null,
    wtn_tennis_id: form.wtn_tennis_id?.trim().toUpperCase() || null,
    guardian_name: includeGuardian ? form.guardian_name?.trim() || null : null,
    guardian_phone: includeGuardian ? guardianPhone || null : null,
    guardian_relationship: includeGuardian ? form.guardian_relationship?.trim() || null : null,
    guardian_email: includeGuardian ? guardianEmail || null : null,
    coach_name: form.coach_name?.trim() || null,
    coach_phone: form.coach_phone?.trim() || null,
    coach_email: form.coach_email?.trim().toLowerCase() || null,
    fee_category: options.feeCategory,
  };
  if (child || options.sendWhoPlays) input.who_plays = options.whoPlays;
  return input;
}

/** ¿La respuesta de este campo personalizado es de la familia (se conserva
 * para el siguiente hijo)? Solo si el organizador lo puso en «Papá, mamá o
 * tutor» o en «Otros», o si nunca eligió y la etiqueta habla del tutor. Un
 * campo viejo sin sección («Talla de playera», «Escuela») es de cada hijo. */
function customAnswerIsFamilyWide(field: RegistrationFormField): boolean {
  if (field.section === "guardian" || field.section === "other") return true;
  if (field.section === "player") return false;
  return suggestsGuardianSection(field);
}

/** «Inscribir a otro jugador»: conserva «Tus datos» (misma cuenta), los
 * consentimientos, el profesor y las respuestas de la familia; limpia al
 * jugador, sus archivos, sus categorías y sus respuestas. El tipo de
 * inscripción y el modo viven fuera del formulario y no se tocan. */
export function resetForAnotherPlayer(form: RegistrationFormState, fields: RegistrationFormField[]): RegistrationFormState {
  const playerCustomKeys = new Set(
    fields.filter((field) => field.kind === "custom" && !customAnswerIsFamilyWide(field)).map((field) => field.key),
  );
  const customAnswers = Object.fromEntries(
    Object.entries(form.custom_answers ?? {}).filter(([key]) => !playerCustomKeys.has(key)),
  );
  return {
    ...initialRegistrationForm,
    email: form.email,
    phone: form.phone,
    guardian_name: form.guardian_name,
    guardian_phone: form.guardian_phone,
    guardian_relationship: form.guardian_relationship,
    guardian_email: form.guardian_email,
    coach_name: form.coach_name,
    coach_phone: form.coach_phone,
    coach_email: form.coach_email,
    data_consent: form.data_consent,
    privacy_notice_accepted: form.privacy_notice_accepted,
    custom_answers: customAnswers,
  };
}

// ---------------------------------------------------------------- confirmación

export function confirmSummaryText({
  whoPlays,
  name,
  age,
  categories,
}: {
  whoPlays: PublicWhoPlays;
  name: string;
  age: number | null;
  categories: string[];
}): string {
  const parts = [name.trim() || "Jugador"];
  if (age !== null) parts.push(`${age} ${age === 1 ? "año" : "años"}`);
  if (categories.length) parts.push(categories.join(", "));
  return whoPlays === "child" ? `Vas a inscribir a ${parts.join(" · ")}` : `Vas a inscribirte: ${parts.join(" · ")}`;
}

// ---------------------------------------------------------------- CURP del jugador

export type CurpDataKey =
  | "first_name"
  | "last_name"
  | "maternal_last_name"
  | "birthdate"
  | "gender"
  | "nationality_code"
  | "state_province";

export type CurpPatch = Partial<Record<CurpDataKey, string>>;

const CURP_NAME_KEYS: CurpDataKey[] = ["first_name", "last_name", "maternal_last_name"];
const CURP_FACT_KEYS: CurpDataKey[] = ["birthdate", "gender", "nationality_code", "state_province"];

/** Una CURP leída y lo que la familia decidió sobre ella. */
export interface CurpUpload {
  /** Lo que dice el PDF, igual que lo lee el clasificador (`curpFactsFromParse`). */
  facts: CurpFacts;
  /** Nombre impreso para comparar con el del papá o la mamá (el partido con
   * la clave; si no hay, el que leyó el servidor cuando es confiable). */
  printedName: CurpNameParts | null;
  /** Lo que dice la CURP (el nombre solo si es confiable). */
  data: CurpPatch;
  /** Nombre para los avisos («Leo López García»). */
  name: string;
  multiple: boolean;
  message: string | null;
  /** Lo que la CURP escribió en el formulario («Subir otra CURP» lo quita). */
  applied: CurpPatch;
  /** «El nombre está bien, continuar» ante «otra persona» (firma). */
  acceptedSignature: string | null;
  /** «El nombre está bien, continuar» ante «Esta CURP es tuya» (firma). */
  guardianAccepted: string | null;
  /** «Conservar lo escrito» en el aviso leve (firma). */
  similarDismissed: string | null;
}

export interface CurpContext {
  whoPlays: PublicWhoPlays;
  guardianName: string | null | undefined;
  /** Día que empieza el torneo (para saber si la CURP es de un menor). */
  startDate?: string | null;
}

export type CurpNotice =
  | { kind: "guardian_curp"; name: string }
  | { kind: "other_person"; name: string; canUseCurp: boolean }
  /** Mismo nombre que la CURP; solo la fecha escrita es otra. */
  | { kind: "birthdate"; date: string }
  | { kind: "similar"; conflicts: Array<{ label: string; typed: string; suggested: string }> };

/** Avisos que detienen «Guardar datos» hasta que la familia elige una de sus
 * opciones (el leve no detiene). */
export function curpNoticeBlocks(notice: CurpNotice | null | undefined): boolean {
  return Boolean(notice && notice.kind !== "similar");
}

function nonEmpty(patch: CurpPatch): CurpPatch {
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => String(value ?? "").trim())) as CurpPatch;
}

function pickKeys(source: CurpPatch, keys: CurpDataKey[]): CurpPatch {
  return nonEmpty(Object.fromEntries(keys.map((key) => [key, source[key]])) as CurpPatch);
}

/** Respuesta de parse-curp → CURP en revisión (null si no trae clave). */
export function curpUploadFromParse(data: PublicCurpParseResponse["data"] | null | undefined): CurpUpload | null {
  const facts = curpFactsFromParse(data);
  if (!data || !facts) return null;
  const parsed = data.parsed;
  const curp = String(parsed.curp ?? "").trim().toUpperCase();
  // Nombre: el partido con la clave como autoridad; si no, el del servidor.
  const split = data.name_split ?? parsed.name_split ?? null;
  const nameReliable = data.name_matches_curp !== false && Boolean(parsed.full_name || split);
  const printedName: CurpNameParts | null =
    facts.name ??
    (nameReliable && (parsed.first_name || parsed.last_name)
      ? { given: parsed.first_name ?? null, paternal: parsed.last_name ?? null, maternal: parsed.maternal_last_name ?? null }
      : null);
  const dataPatch = nonEmpty({
    first_name: nameReliable ? split?.first_name ?? parsed.first_name ?? "" : "",
    last_name: nameReliable ? split?.last_name ?? parsed.last_name ?? "" : "",
    maternal_last_name: nameReliable ? split?.maternal_last_name ?? parsed.maternal_last_name ?? "" : "",
    birthdate: parsed.birthdate ?? "",
    gender: parsed.gender ?? "",
    nationality_code: parsed.nationality_code ?? "",
    state_province: parsed.state_province ?? "",
  });
  const printed =
    data.raw_full_name ||
    parsed.raw_full_name ||
    parsed.full_name ||
    [split?.first_name, split?.last_name, split?.maternal_last_name].filter(Boolean).join(" ") ||
    curp;
  return {
    facts,
    printedName,
    data: dataPatch,
    name: displayPersonName(printed),
    multiple: Boolean(data.multiple_curps),
    message: data.message ?? null,
    applied: {},
    acceptedSignature: null,
    guardianAccepted: null,
    similarDismissed: null,
  };
}

export function typedOf(form: Pick<RegistrationFormState, "first_name" | "last_name" | "maternal_last_name" | "birthdate" | "gender">): CurpRegistered {
  return {
    first_name: form.first_name,
    last_name: form.last_name,
    maternal_last_name: form.maternal_last_name ?? "",
    birthdate: form.birthdate,
    gender: form.gender,
  };
}

function hasTypedName(typed: CurpRegistered): boolean {
  return Boolean(String(typed.first_name ?? "").trim() && String(typed.last_name ?? "").trim());
}

function guardianSignature(upload: CurpUpload, guardianName: string | null | undefined): string {
  return `${upload.facts.letters ?? ""}|${tokens(guardianName).join(" ")}`;
}

function guardianCheckOpen(upload: CurpUpload, context: CurpContext): boolean {
  return (
    context.whoPlays === "child" &&
    curpLooksLikeGuardian({ ...upload.facts, name: upload.printedName }, context.guardianName, context.startDate) &&
    upload.guardianAccepted !== guardianSignature(upload, context.guardianName)
  );
}

/** «Otra persona» solo por la fecha: con la fecha en blanco, el nombre
 * escrito coincide (o se parece) con el de la CURP. Es un error al escribir
 * la fecha, no la CURP de alguien más. */
function onlyBirthdateDiffers(upload: CurpUpload, typed: CurpRegistered, reasons: string[]): boolean {
  if (reasons.length !== 1 || reasons[0] !== "birthdate_differs") return false;
  const status = classifyCurp(upload.facts, { ...typed, birthdate: "" }).status;
  return status === "matches" || status === "similar";
}

function nameConflicts(upload: CurpUpload, typed: CurpRegistered) {
  const labels: Array<[CurpDataKey, keyof CurpRegistered, string]> = [
    ["first_name", "first_name", "Nombre"],
    ["last_name", "last_name", "Apellido paterno"],
    ["maternal_last_name", "maternal_last_name", "Apellido materno"],
  ];
  return labels.flatMap(([dataKey, typedKey, label]) => {
    const typedValue = String(typed[typedKey] ?? "").trim();
    const suggested = String(upload.data[dataKey] ?? "").trim();
    return typedValue && suggested && !sameNameText(typedValue, suggested) ? [{ label, typed: typedValue, suggested }] : [];
  });
}

/** Aviso a mostrar EN VIVO (cambia al escribir el nombre, la fecha o el género). */
export function curpNotice(upload: CurpUpload | null, typed: CurpRegistered, context: CurpContext): CurpNotice | null {
  if (!upload) return null;
  if (guardianCheckOpen(upload, context)) return { kind: "guardian_curp", name: upload.name };
  if (!hasTypedName(typed)) return null;
  const signature = curpReviewSignature(upload.facts, typed);
  const { status, reasons } = classifyCurp(upload.facts, typed);
  if (status === "other_person") {
    if (upload.acceptedSignature === signature) return null;
    const curpDate = upload.data.birthdate || upload.facts.birthdate || "";
    if (curpDate && onlyBirthdateDiffers(upload, typed, reasons)) return { kind: "birthdate", date: curpDate };
    return { kind: "other_person", name: upload.name, canUseCurp: Boolean(upload.data.first_name && upload.data.last_name) };
  }
  if (status === "similar" && upload.similarDismissed !== signature) {
    const conflicts = nameConflicts(upload, typed);
    if (conflicts.length) return { kind: "similar", conflicts };
  }
  return null;
}

/** Lo que la CURP llena, ya pasada la revisión del tutor. Sin nombre escrito:
 * nombre (solo lo vacío), fecha, género y estado. Con nombre: fecha, género y
 * estado si coincide o se parece; NADA si parece de otra persona. */
function patchAfterGuardianCheck(upload: CurpUpload, typed: CurpRegistered): CurpPatch {
  const facts = pickKeys(upload.data, CURP_FACT_KEYS);
  if (!hasTypedName(typed)) {
    // Solo lo vacío del nombre; si con eso ya parece de otra persona (p. ej.
    // escribió otra fecha), no se toca nada.
    const names = pickKeys(upload.data, CURP_NAME_KEYS.filter((key) => !String(typed[key as keyof CurpRegistered] ?? "").trim()));
    const filled = { ...typed, ...names };
    if (hasTypedName(filled) && classifyCurp(upload.facts, filled).status === "other_person") return {};
    return { ...names, ...facts };
  }
  const status = classifyCurp(upload.facts, typed).status;
  return status === "other_person" ? {} : facts;
}

/** Al subir la CURP: qué se escribe en el formulario. Nunca toma fecha ni
 * género de una CURP que parece de otra persona (ni la del papá o la mamá). */
export function curpPatchOnUpload(upload: CurpUpload, typed: CurpRegistered, context: CurpContext): CurpPatch {
  if (guardianCheckOpen(upload, context)) return {};
  return patchAfterGuardianCheck(upload, typed);
}

/** «Usar datos de la CURP»: todo lo que dice la CURP. */
export function curpPatchUseAll(upload: CurpUpload): CurpPatch {
  return { ...upload.data };
}

/** «Usar la fecha de la CURP» (solo la fecha era otra): la fecha y, de lo
 * demás que dice la CURP, solo lo que sigue vacío. */
export function curpPatchUseDate(upload: CurpUpload, form: Pick<RegistrationFormState, CurpDataKey>): CurpPatch {
  const facts = pickKeys(upload.data, CURP_FACT_KEYS);
  return Object.fromEntries(
    Object.entries(facts).filter(([key]) => key === "birthdate" || !String(form[key as CurpDataKey] ?? "").trim()),
  ) as CurpPatch;
}

/** «Usar datos del documento» del aviso leve: solo el nombre. */
export function curpPatchUseName(upload: CurpUpload): CurpPatch {
  return pickKeys(upload.data, CURP_NAME_KEYS);
}

/** «Subir otra CURP»: quita del formulario lo que esa CURP escribió y nadie
 * cambió después. */
export function curpPatchRevert(upload: CurpUpload | null, form: RegistrationFormState): Partial<RegistrationFormState> {
  if (!upload) return {};
  const revert: Partial<RegistrationFormState> = {};
  (Object.keys(upload.applied) as CurpDataKey[]).forEach((key) => {
    if (String(form[key] ?? "") === String(upload.applied[key] ?? "")) {
      (revert as Record<string, unknown>)[key] = initialRegistrationForm[key] ?? "";
    }
  });
  return revert;
}

/** «El nombre está bien, continuar». Devuelve la CURP con la firma guardada y
 * lo que toca llenar después (solo tras el aviso de «Esta CURP es tuya»: la
 * familia dice que la CURP sí es del jugador). */
export function acceptCurpNotice(
  upload: CurpUpload,
  typed: CurpRegistered,
  context: CurpContext,
): { upload: CurpUpload; patch: CurpPatch } {
  if (guardianCheckOpen(upload, context)) {
    const accepted = { ...upload, guardianAccepted: guardianSignature(upload, context.guardianName) };
    const patch = patchAfterGuardianCheck(accepted, typed);
    return { upload: accepted, patch };
  }
  return { upload: { ...upload, acceptedSignature: curpReviewSignature(upload.facts, typed) }, patch: {} };
}

/** «Conservar lo escrito» en el aviso leve. */
export function dismissSimilarNotice(upload: CurpUpload, typed: CurpRegistered): CurpUpload {
  return { ...upload, similarDismissed: curpReviewSignature(upload.facts, typed) };
}

/** Registra lo que se escribió desde la CURP. */
export function withApplied(upload: CurpUpload, patch: CurpPatch): CurpUpload {
  return Object.keys(patch).length ? { ...upload, applied: { ...upload.applied, ...patch } } : upload;
}
